import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { LightBox } from "@liquid-design/liquid-design-react";
import {
  LdButton,
  LdLoading,
  LdTypo,
} from "@emdgroup-liquid/liquid/dist/react";
import moment from "moment";

import { PureSurveyLayout } from "layouts";
import { useDispatch, useSelector } from "store";
import { getSurveyById, submitAnswer, selectSurveyById } from "services/survey";
import QuestionForm from "components/Question/QuestionForm";

import { SurveyAnswer } from "typings/api/types";
import { setSurveyAnswered, alreadyAnswered } from "util/cookie";

import { useDeviceType } from "hooks/useMediaQuery";

const AnswerSurvey = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [answers, setAnswer] = useState<SurveyAnswer>({});
  const [initialized, setInitialized] = useState(false);
  const [searchParams] = useSearchParams();
  const { status, error } = useSelector((state) => state.survey);
  const surveyId = searchParams.get("survey") || "";
  const survey = useSelector(selectSurveyById(surveyId));

  const { isMobile } = useDeviceType();
  const isLoading = status === "loading" || status === "idle";

  useEffect(() => {
    if (surveyId !== null) {
      dispatch(getSurveyById(surveyId))
        .then(({ payload }) => {
          setInitialized(true);
          if (
            alreadyAnswered(
              payload.survey.surveyId,
              payload.survey.currentIteration
            )
          ) {
            navigate("success", {
              state: {
                searchParams: { message: "You already answered this survey" },
              },
            });
          }
        })
        .catch(() => setInitialized(true));
    }
  }, [surveyId]);

  useEffect(() => {
    if (!isLoading && survey === undefined) {
      navigate("error", {
        state: {
          searchParams: { error: "404 - Survey does not exist" },
        },
      });
    }
  }, [isLoading, survey]);

  const handleChangeAnswer = (questionId: string, answer: SurveyAnswer) => {
    setAnswer((state) => ({
      ...state,
      [questionId]: {
        questionId,
        ...answer,
      },
    }));
  };

  const handleSubmit = async () => {
    try {
      if (surveyId !== null) {
        const response = await dispatch(submitAnswer({ surveyId, answers }));
        if ((response as any).error === undefined) {
          setSurveyAnswered(survey.surveyId, survey.currentIteration);
          navigate("success");
        } else {
          navigate("error");
        }
      }
    } catch (err) {
      navigate("error");
    }
  };

  return (
    <PureSurveyLayout>
      <div className="container flex-col p-ld-32">
        <div className="container">
          <LdTypo variant="h1">{survey && survey.surveyName}</LdTypo>
          <LdTypo>
            Survey ends at {moment(survey?.endDate).format("ddd, DD.MM.YYYY")}
          </LdTypo>
        </div>
        <div className="container flex flex-col divide-y-2 divide-neutral-010 my-ld-32">
          {survey !== undefined &&
            survey?.questions.map(
              ({
                questionId = "",
                questionType,
                mandatory,
                questionText,
                options,
              }) => {
                return (
                  <QuestionForm
                    key={questionId}
                    disabled={isLoading}
                    questionType={questionType}
                    questionText={questionText}
                    options={options || []}
                    mandatory={mandatory}
                    onChange={(answer: SurveyAnswer) =>
                      handleChangeAnswer(questionId, answer)
                    }
                  />
                );
              }
            )}
        </div>
        <LdButton
          className="mb-ld-32 w-full md:w-max"
          disabled={isLoading}
          progress={isLoading ? "pending" : undefined}
          onClick={handleSubmit}
        >
          Submit
        </LdButton>
        {isMobile && <div className="flex h-24" />}
      </div>
      <LightBox open={!initialized} onClose={() => {}}>
        <div className="flex justify-center items-center">
          <LdLoading />
        </div>
      </LightBox>
    </PureSurveyLayout>
  );
};

export default AnswerSurvey;
